import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';

import { commonPacksContainerQuery } from 'components/commonCard/CommonPacksPage/utils';

import { PackWithBuyButton } from '../PackWithBuyButton';
import { useItemToDisplay } from '../useItemToDisplay';
import type { BasicPacks_cardPack } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  @container ${commonPacksContainerQuery} {
    border-radius: var(--double-unit);
    border: 1px solid var(--c-nd-150);
    padding: var(--double-unit) var(--quadruple-unit);
    overflow: hidden;
  }
  justify-content: space-between;
`;

const PacksList = styled(Horizontal)`
  justify-content: space-between;
`;

type Props = {
  cardPacks: BasicPacks_cardPack[];
  cardPackGroupId: string;
  onClickBuy: (cardPackGroupId: string, slug: string) => void;
};
export const BasicPacks = ({
  cardPacks,
  cardPackGroupId,
  onClickBuy,
}: Props) => {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const isWideContainer = width > 420;
  const { ref: scrollableWrapperRef, itemToDisplay } = useItemToDisplay();
  if (!cardPacks.length) {
    return null;
  }

  return (
    <Root ref={ref}>
      <Vertical gap={0.5}>
        <BrandM>
          <FormattedMessage
            id="BasicPacks.title"
            defaultMessage="Basic Packs"
          />
        </BrandM>
        <LabelL
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color':
                'linear-gradient(315deg, #868a8a 14.64%, #e8ecee 85.36%)',
            } as CSSProperties
          }
        >
          {cardPacks.at(0)?.competition?.displayName}
        </LabelL>
      </Vertical>
      {isWideContainer ? (
        <PacksList>
          {cardPacks.map(pack => (
            <NeonEdge
              key={pack.slug}
              color={pack.primaryColor}
              inset="0 0 -17px 0"
              showHalo="never"
              showNeon="hover"
            >
              <PackWithBuyButton
                cardPack={pack}
                cardPackGroupId={cardPackGroupId}
                onClickBuy={onClickBuy}
                gap={3}
                scaleOnHover={false}
              />
            </NeonEdge>
          ))}
        </PacksList>
      ) : (
        <div ref={scrollableWrapperRef}>
          <Scrollable
            itemToDisplay={itemToDisplay}
            overhang={itemToDisplay === 1 ? 0 : 6}
          >
            {cardPacks.map(pack => (
              <PackWithBuyButton
                key={pack.slug}
                cardPack={pack}
                cardPackGroupId={cardPackGroupId}
                onClickBuy={onClickBuy}
                gap={3}
              />
            ))}
          </Scrollable>
        </div>
      )}
    </Root>
  );
};

BasicPacks.fragments = {
  cardPack: gql`
    fragment BasicPacks_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      competition {
        slug
        displayName
      }
      ...PackWithBuyButton_cardPack
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<BasicPacks_cardPack>,
};
